import { useState } from "react";
import { Send, CheckCircle2, Mail, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import PublicPageLayout from "@/components/PublicPageLayout";
import api, { formatApiErrorDetail } from "@/lib/api";

const TOPICS = [
  { value: "general", label: "General inquiry" },
  { value: "assessment", label: "Assessment help" },
  { value: "subscription", label: "Subscription & billing" },
  { value: "jobs", label: "Job matching" },
  { value: "government_support", label: "Government support guidance" },
  { value: "privacy", label: "Privacy or data request" },
  { value: "feedback", label: "Feedback or feature request" },
  { value: "other", label: "Other" },
];

const EMPTY = { name: "", email: "", phone: "", topic: "general", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [sentId, setSentId] = useState(null);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error("Please add a few more details to your message (10+ characters).");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        topic: form.topic,
        message: form.message.trim(),
      });
      setSentId(data.submission_id);
      setForm(EMPTY);
      toast.success("Message sent — we'll be in touch.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicPageLayout
      eyebrow="Contact"
      title="Get in touch"
      intro="Questions, feedback, a tricky situation we should know about? Send us a note — a real person reads every message."
      testid="contact-page"
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <aside className="space-y-6 lg:col-span-1" data-testid="contact-aside">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-3 text-slate-900">
              <Mail className="h-4 w-4 text-sky-700" />
              <span className="text-sm font-medium">Email us directly</span>
            </div>
            <p className="mt-2 text-sm text-slate-600">hello@aitookmyjob.example</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-3 text-slate-900">
              <Clock className="h-4 w-4 text-sky-700" />
              <span className="text-sm font-medium">Response time</span>
            </div>
            <p className="mt-2 text-sm text-slate-600">
              Within 2 business days, usually much faster.
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-3 text-slate-900">
              <MapPin className="h-4 w-4 text-sky-700" />
              <span className="text-sm font-medium">Based in</span>
            </div>
            <p className="mt-2 text-sm text-slate-600">Sydney, Australia.</p>
          </div>
        </aside>

        <div className="lg:col-span-2">
          {sentId ? (
            <div
              className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center"
              data-testid="contact-success"
            >
              <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
              <h2 className="mt-4 text-xl font-medium text-slate-900">Message received</h2>
              <p className="mt-2 text-slate-600 max-w-md mx-auto">
                Thanks — we&apos;ve logged your message and will reply by email shortly. Reference{" "}
                <span className="font-mono text-xs">{sentId}</span>.
              </p>
              <button
                type="button"
                onClick={() => setSentId(null)}
                className="mt-6 inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                data-testid="contact-send-another-button"
              >
                Send another
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8 space-y-5"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Your name" required>
                  <input
                    type="text"
                    required
                    minLength={1}
                    maxLength={120}
                    value={form.name}
                    onChange={update("name")}
                    className="form-input"
                    placeholder="Jane Doe"
                    data-testid="contact-name-input"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    className="form-input"
                    placeholder="jane@example.com"
                    data-testid="contact-email-input"
                  />
                </Field>
                <Field label="Phone (optional)">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="form-input"
                    placeholder="+61 ..."
                    data-testid="contact-phone-input"
                  />
                </Field>
                <Field label="Topic" required>
                  <select
                    required
                    value={form.topic}
                    onChange={update("topic")}
                    className="form-input"
                    data-testid="contact-topic-select"
                  >
                    {TOPICS.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Message" required>
                <textarea
                  required
                  minLength={10}
                  maxLength={4000}
                  value={form.message}
                  onChange={update("message")}
                  rows={7}
                  className="form-input resize-y"
                  placeholder="Tell us what's going on. The more context, the better we can help."
                  data-testid="contact-message-textarea"
                />
                <p className="text-xs text-slate-400 mt-1">
                  {form.message.length}/4000 — minimum 10 characters
                </p>
              </Field>

              <div className="flex items-center justify-between gap-4 pt-2">
                <p className="text-xs text-slate-500 max-w-sm">
                  By sending you agree to our{" "}
                  <a href="/privacy" className="underline text-slate-700">
                    Privacy Policy
                  </a>
                  .
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white shadow-md hover:bg-slate-800 hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  data-testid="contact-submit-button"
                >
                  {submitting ? "Sending..." : "Send message"}
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          border: 1px solid rgb(226 232 240);
          border-radius: 0.75rem;
          padding: 0.625rem 0.875rem;
          background: white;
          font-size: 0.95rem;
          color: rgb(15 23 42);
          outline: none;
          transition: border-color .15s ease, box-shadow .15s ease;
        }
        .form-input:focus {
          border-color: rgb(2 132 199);
          box-shadow: 0 0 0 3px rgba(2,132,199,0.12);
        }
      `}</style>
    </PublicPageLayout>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </span>
      {children}
    </label>
  );
}
